import { useContext, useMemo } from 'react';
import { context, Span, SpanOptions, trace } from '@opentelemetry/api';
import { OTELContext } from '../context/otel/ctx';
import { OTELContextProps } from '../models/context/otel';
import { recordSpanError } from '../utils/otel/enhanceTrace';
import { sanitizeSpanAttributes } from '../utils/otel/sanitizeSpanAttributes';

type UseOTELOptions = {
  context: Partial<OTELContextProps>;
  startSpan: (
    spanName: string,
    attributes?: Record<string, string | number | boolean>,
    options?: SpanOptions,
  ) => Span;
  addEvent: (eventName: string, attributes?: Record<string, string | number | boolean>) => void;
  recordError: (error: unknown) => void;
};

export const useOTEL = (): UseOTELOptions => {
  const otelContext = useContext(OTELContext);

  const tracerName = useMemo(
    () => otelContext.serviceName ?? `olr-${otelContext.brandCode ?? 'client'}`,
    [otelContext.brandCode, otelContext.serviceName],
  );

  const tracer = useMemo(
    () => trace.getTracer(tracerName, otelContext.version),
    [otelContext.version, tracerName],
  );

  const startSpan: UseOTELOptions['startSpan'] = (spanName, attributes = {}, options) => {
    const span = tracer.startSpan(spanName, options, context.active());
    const safeAttributes = sanitizeSpanAttributes(attributes);

    for (const [key, value] of Object.entries(safeAttributes)) {
      span.setAttribute(key, value);
    }

    return span;
  };

  const addEvent: UseOTELOptions['addEvent'] = (eventName, attributes = {}) => {
    const span = trace.getActiveSpan();
    if (!span) {
      return;
    }

    span.addEvent(eventName, sanitizeSpanAttributes(attributes));
  };

  const recordError: UseOTELOptions['recordError'] = (error) => {
    recordSpanError(trace.getActiveSpan(), error);
  };

  return {
    context: otelContext,
    startSpan,
    addEvent,
    recordError,
  };
};
